/**
 * [TEMP: legacy-search back-compat] Detection and parsing primitives for the
 * retired client-side search's v1 hash encoding
 * (`/search#q=trees&cf-ec_category=3D`), the inbound-link shim kept for old
 * bookmarks, CMS banner URLs and legacySearchRedirect output. Shared by the
 * migration shim (SearchV2/ssr/headless/legacyHashParams) and
 * SearchResultsBanner, which arbitrates between hash and query-string state.
 * Dependency-free on purpose: SearchResultsBanner must not pull
 * `@coveo/headless-react/ssr` in transitively.
 *
 * TODO: Remove alongside legacyHashParams once inbound hash links are gone.
 */

/** Non-facet Coveo search params carried in the legacy hash. */
const COVEO_SCALAR_PARAMS = new Set([
  'q',
  'aq',
  'cq',
  'tab',
  'debug',
  'sortCriteria',
  'firstResult',
  'numberOfResults',
  'enableQuerySyntax',
]);

/** Coveo facet param prefixes (`f-ec_category`, `nf-ec_price_filter`, ...). */
const COVEO_FACET_PREFIX = /^(f|fExcluded|af|cf|nf|df|sf|mnf|mdf)-.+/;

export const isLegacyCoveoParam = (key: string): boolean =>
  COVEO_SCALAR_PARAMS.has(key) || COVEO_FACET_PREFIX.test(key);

/**
 * A raw `key=value` hash segment. Values stay percent-ENCODED at this stage:
 * the v1 serializer encodes each value individually and joins them with
 * literal commas, so an encoded `%2C` INSIDE a value (e.g.
 * `f-publisher_name=Foo%2C%20Inc.`) is only distinguishable from a multi-value
 * delimiter before decoding. URLSearchParams can't parse this format — it
 * decodes before any split, and it also reads `+` as a space where the v1
 * encoding means a literal plus.
 */
export type LegacyHashSegment = { raw: string; key: string; rawValue: string };

export const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export function readLegacyHashSegments(): LegacyHashSegment[] {
  if (typeof window === 'undefined') {
    return [];
  }
  const { hash } = window.location;
  const content = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!content) {
    return [];
  }
  return content.split('&').map((raw) => {
    const separator = raw.indexOf('=');
    return separator === -1
      ? { raw, key: safeDecode(raw), rawValue: '' }
      : {
          raw,
          key: safeDecode(raw.slice(0, separator)),
          rawValue: raw.slice(separator + 1),
        };
  });
}

/** True when the current URL hash carries any v1-style Coveo param. */
export function hasLegacyCoveoHash(): boolean {
  return readLegacyHashSegments().some(({ key }) => isLegacyCoveoParam(key));
}
